import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calendar,
  Truck,
  CheckCircle,
  XCircle,
  DollarSign,
  ChevronRight,
} from "lucide-react";

export default function DeliveryHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaInicio, setFechaInicio] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  useEffect(() => {
    fetchHistory();
  }, [fechaInicio, fechaFin]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/despachos/domiciliario/historial?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C8902A]"></div>
      </div>
    );
  }

  const resumen = data?.resumen || {};
  const dispatches = data?.dispatches || [];

  return (
    <div className="p-4 space-y-4" data-testid="delivery-history">
      <h1 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-bold">
        Mi Historial
      </h1>

      {/* Filtros de fecha */}
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-xs text-[#4A2C0A]/60">Desde</label>
          <Input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="border-[#E5D4B3]"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs text-[#4A2C0A]/60">Hasta</label>
          <Input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="border-[#E5D4B3]"
          />
        </div>
      </div>

      {/* Resumen */}
      <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
        <h2 className="text-sm font-semibold text-[#4A2C0A] mb-3">Resumen del Período</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#FBF5E8] rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[#4A2C0A]">{resumen.total_rutas}</p>
            <p className="text-xs text-[#4A2C0A]/60">Rutas</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{resumen.total_delivered}</p>
            <p className="text-xs text-green-600">Entregados</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{resumen.total_failed}</p>
            <p className="text-xs text-red-600">No Entregados</p>
          </div>
          <div className="bg-[#C8902A]/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[#C8902A]">
              ${(resumen.total_collected || 0).toLocaleString("es-CO")}
            </p>
            <p className="text-xs text-[#C8902A]">Recaudado</p>
          </div>
        </div>
      </div>

      {/* Lista de Rutas */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-[#4A2C0A]">Rutas ({dispatches.length})</h2>
        
        {dispatches.length === 0 ? (
          <div className="bg-white rounded-xl p-8 border border-[#E5D4B3] text-center">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
            <p className="text-[#4A2C0A]">No hay rutas en este período</p>
          </div>
        ) : (
          dispatches.map((dispatch) => (
            <div
              key={dispatch.id}
              className="bg-white rounded-xl border border-[#E5D4B3] p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-semibold text-[#4A2C0A]">{dispatch.name}</p>
                  <p className="text-xs text-[#4A2C0A]/60">
                    {dispatch.dispatch_date} • {dispatch.time_window}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  dispatch.status === "completed" 
                    ? "bg-green-100 text-green-700" 
                    : dispatch.status === "partially_completed"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-gray-100 text-gray-700"
                }`}>
                  {dispatch.status === "completed" ? "Completada" : 
                   dispatch.status === "partially_completed" ? "Parcial" : 
                   dispatch.status}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {dispatch.orders_delivered}
                </div>
                <div className="flex items-center gap-1 text-red-600">
                  <XCircle className="w-4 h-4" />
                  {dispatch.orders_failed}
                </div>
                <div className="flex items-center gap-1 text-[#C8902A]">
                  <DollarSign className="w-4 h-4" />
                  ${(dispatch.total_cash_to_collect || 0).toLocaleString("es-CO")}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
